import type React from 'react';
import { Anchor } from '@zillow/constellation-10';
import * as linkify from 'linkifyjs';
import { normalizeUrl } from './urlUtils';

/**
 * Represents a parsed chunk of text.
 */
type ParsedChunk =
  | { type: 'text'; content: string }
  | { type: 'url'; content: string; href: string; displayText: string };

/**
 * Parses raw text and extracts URLs and plain text into structured chunks using linkifyjs.
 */
function parseTextToChunks(text: string): Array<ParsedChunk> {
  const chunks: Array<ParsedChunk> = [];

  if (!text) return [];

  // Use linkifyjs to find all URLs in the text
  const matches = linkify.find(text, 'url');

  let lastIndex = 0;

  for (const match of matches) {
    const start = match.start;
    const end = match.end;
    const url = match.value;

    // Add any preceding plain text
    if (start > lastIndex) {
      chunks.push({ type: 'text', content: text.slice(lastIndex, start) });
    }

    // Normalize the URL
    const href = normalizeUrl(url);
    if (href) {
      chunks.push({ type: 'url', content: url, href, displayText: url });
    } else {
      // If normalization fails, treat as plain text
      chunks.push({ type: 'text', content: url });
    }

    lastIndex = end;
  }

  // Add any remaining text after the last match
  if (lastIndex < text.length) {
    chunks.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return chunks;
}

/**
 * Renders parsed chunks into React nodes with Anchor tags for URLs.
 * Uses onImpact prop for links that need high contrast on dark backgrounds.
 * If onLinkClick is provided, it will be called instead of the default link behavior.
 */
function renderChunks(
  chunks: Array<ParsedChunk>,
  onImpact?: boolean,
  onLinkClick?: (url: string, displayText?: string) => void,
): Array<React.ReactNode> {
  let keyIndex = 0;
  return chunks.map((chunk) => {
    if (chunk.type === 'url') {
      if (onLinkClick) {
        // Custom link handler - prevent default navigation and call the handler
        return (
          <Anchor
            key={`link-${keyIndex++}`}
            href={chunk.href}
            onImpact={onImpact}
            onClick={(e) => {
              e.preventDefault();
              onLinkClick(chunk.href, chunk.displayText);
            }}
          >
            {chunk.displayText}
          </Anchor>
        );
      }

      // Default behavior - open in new tab
      return (
        <Anchor
          key={`link-${keyIndex++}`}
          href={chunk.href}
          target="_blank"
          rel="noopener noreferrer"
          onImpact={onImpact}
        >
          {chunk.displayText}
        </Anchor>
      );
    }
    return chunk.content;
  });
}

/**
 * Converts a string with URLs into an array of React nodes with clickable links.
 * Uses linkifyjs for robust URL detection.
 *
 * @param text - The text to parse for URLs
 * @param onImpact - Whether to use high contrast styling for links on dark backgrounds
 * @param onLinkClick - Optional custom handler for link clicks (prevents default navigation)
 */
export function parseTextWithLinks(
  text: string,
  onImpact?: boolean,
  onLinkClick?: (url: string, displayText?: string) => void,
): Array<React.ReactNode> {
  if (!text) return [''];

  const chunks = parseTextToChunks(text);
  return chunks.length > 0 ? renderChunks(chunks, onImpact, onLinkClick) : [text];
}
